import { useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { useEmailTemplates } from "../../settings-communication/api/hooks";
import type { EmailTemplate } from "../../settings-communication/api/types";
import { useInboxEmbed } from "../context/useInboxEmbed";

interface TemplatePickerButtonProps {
  onSelect: (template: EmailTemplate) => void;
  disabled?: boolean;
}

export const TemplatePickerButton = ({
  onSelect,
  disabled
}: TemplatePickerButtonProps) => {
  const embed = useInboxEmbed();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useEmailTemplates({ per_page: 50 });
  const templates = data?.items ?? [];

  const handleSelect = (template: EmailTemplate) => {
    setOpen(false);
    onSelect(template);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} placement="top-end">
      <PopoverTrigger
        onClick={(e) => {
          if (disabled) return;
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          disabled={disabled}
          className="!text-sm !font-bold shadow-xs"
        >
          Templates
        </Button>
      </PopoverTrigger>

      <PopoverContent className={embed?.menuZClass ?? "z-[12]"}>
        <div className="w-[280px] overflow-hidden rounded-lg border border-alpha-100 bg-white shadow-[0_8px_8px_-4px_rgba(28,29,34,0.03),0_20px_24px_-4px_rgba(28,29,34,0.08)]">
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-content-400">Loading...</div>
          ) : null}

          {!isLoading && templates.length === 0 ? (
            <div className="px-4 py-3 text-sm text-content-400">
              No templates available
            </div>
          ) : null}

          {!isLoading && templates.length > 0 ? (
            <div className="max-h-[240px] overflow-y-auto">
              {templates.map((template, index) => (
                <div key={template.id}>
                  {index > 0 ? (
                    <div className="h-px w-full bg-alpha-40" />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => handleSelect(template)}
                    className="block w-full truncate px-4 py-2.5 text-left font-medium text-content-1000 transition-colors hover:bg-alpha-40"
                  >
                    {template.name}
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
};
